import React, { useState } from 'react';
import { groupReadiness, reasonOk, TM_REASON_MIN } from '../../utils/tmPricing';

const MAX_SUB_ITEMS = 5;

// Entry keys never shown: the raw (unclamped) shop-error formula is not a price (plan (d): NEVER
// override_raw / raw), and snapshots / hashes are bookkeeping.
const HIDDEN_KEY = /(^|_)raw($|_)|snapshot|hash/i;
const isScalar = (v) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

/**
 * One collector entry as words: its own message / detail / label when it has one; otherwise its
 * scalar fields ("defect id: 77"), scalar lists joined, lists of objects counted ("candidates: 1"),
 * nested objects and hidden keys left out (never "[object Object]").
 */
export function subItemText(it) {
  if (it == null) return '';
  if (typeof it !== 'object') return String(it);
  const own = [it.message, it.detail, it.label].find((x) => typeof x === 'string' && x.trim());
  if (own) return own;
  const parts = [];
  Object.entries(it).forEach(([k, v]) => {
    if (HIDDEN_KEY.test(k) || v === null || v === undefined || v === '') return;
    const name = k.replace(/_/g, ' ');
    if (Array.isArray(v)) {
      if (v.length === 0) return;
      parts.push(v.every(isScalar) ? `${name}: ${v.join(', ')}` : `${name}: ${v.length}`);
    } else if (isScalar(v)) {
      parts.push(`${name}: ${v}`);
    }
  });
  return parts.join(', ');
}

function SubItems({ items }) {
  const list = (Array.isArray(items) ? items : []).filter((it) => subItemText(it) !== '');
  if (!list.length) return null;
  const shown = list.slice(0, MAX_SUB_ITEMS);
  return (
    <ul className="mb-0 ps-3 small">
      {shown.map((it, i) => <li key={i}>{subItemText(it)}</li>)}
      {list.length > MAX_SUB_ITEMS && <li className="text-muted">+{list.length - MAX_SUB_ITEMS} more</li>}
    </ul>
  );
}

/**
 * The order's readiness for Approve T&M Pricing (tm_pricing.READINESS_SEVERITY):
 *   hard    red — must be fixed on the order; there is no override;
 *   ack     amber — needs a reason (>= 5 visible characters), green once it has one;
 *   info    grey — shown only;
 *   warning amber, listed apart (never blocks).
 * ackCodes limits which acknowledgements this basis may send (a MANUAL draft: the 1c codes only).
 * warnings: extra warning items listed with the item warnings (tm_line_actuals' own `warnings`).
 * actions(item): optional shortcut buttons for an item (e.g. Confirm link / Re-confirm / Undo).
 */
export default function TmReadinessList({ items, acks = {}, onAck, ackCodes = null, warnings = null, actions = null }) {
  const [showInfo, setShowInfo] = useState(false);
  const all = [...(Array.isArray(items) ? items : []), ...(Array.isArray(warnings) ? warnings : [])];
  const g = groupReadiness(all, acks);
  const act = (it) => (typeof actions === 'function' ? actions(it) : null);
  const none = !g.hard.length && !g.ack.length && !g.info.length && !g.warning.length;
  if (none) {
    return <div className="alert alert-success py-2 small mb-2"><i className="bi bi-check-circle me-1"></i>Ready: nothing blocks pricing.</div>;
  }
  return (
    <div className="mb-2" data-testid="tm-readiness">
      {g.hard.map((it) => (
        <div key={`h-${it.code}`} className="alert alert-danger py-2 small mb-1" data-code={it.code} data-testid={`tm-ready-hard-${it.code}`}>
          <strong><i className="bi bi-x-octagon me-1"></i>{it.label}</strong>
          {it.detail && <span> — {it.detail}</span>}
          <div className="text-danger-emphasis" style={{ fontSize: '0.75rem' }}>Fix this on the order first; it can't be overridden.</div>
          <SubItems items={it.items} />
          {act(it)}
        </div>
      ))}
      {g.ack.map((it) => {
        const allowed = !ackCodes || ackCodes.includes(it.code);
        const reason = acks[it.code] || '';
        const ok = allowed && reasonOk(reason);
        return (
          <div key={`a-${it.code}`} className={`alert ${ok ? 'alert-success' : 'alert-warning'} py-2 small mb-1`} data-code={it.code} data-testid={`tm-ready-ack-${it.code}`}>
            <strong><i className={`bi ${ok ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>{it.label}</strong>
            {it.detail && <span> — {it.detail}</span>}
            <SubItems items={it.items} />
            {act(it)}
            {allowed ? (
              <input
                type="text"
                className={`form-control form-control-sm mt-1 ${ok ? 'is-valid' : ''}`}
                placeholder={`Why is this OK? (at least ${TM_REASON_MIN} characters)`}
                aria-label={`Reason: ${it.label}`}
                value={reason}
                onChange={(e) => onAck && onAck(it.code, e.target.value)}
              />
            ) : (
              <div className="mt-1 text-muted">Can't be acknowledged when pricing by hand: fix it on the order.</div>
            )}
          </div>
        );
      })}
      {g.warning.length > 0 && (
        <div className="alert alert-warning py-2 small mb-1" data-testid="tm-ready-warnings">
          <strong>Warnings (they don't block)</strong>
          <ul className="mb-0 ps-3">
            {g.warning.slice(0, MAX_SUB_ITEMS).map((it, i) => (
              <li key={`w-${it.code}-${i}`}>{it.label}{it.detail ? ` — ${it.detail}` : ''}{act(it)}</li>
            ))}
            {g.warning.length > MAX_SUB_ITEMS && <li className="text-muted">+{g.warning.length - MAX_SUB_ITEMS} more</li>}
          </ul>
        </div>
      )}
      {g.info.length > 0 && (
        <div className="small text-muted mb-1">
          <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => setShowInfo((v) => !v)}>
            {showInfo ? 'Hide' : 'Show'} {g.info.length} note{g.info.length === 1 ? '' : 's'}
          </button>
          {showInfo && (
            <ul className="mb-0 ps-3">
              {g.info.map((it) => (
                <li key={`i-${it.code}`}>{it.label}{it.detail ? ` — ${it.detail}` : ''}<SubItems items={it.items} /></li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
